
import { Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import CustomSelect from "../../../../components/Select/Select";

const allObj = { name: 'All', value: 'all' }

const RevenueByServiceChart = () => {
  const dispatch = useDispatch();
  const { revenuebyTop5, loading, top5ServicesByRevenue } = useSelector((state) => state.serviceDashboard);
  const [timeRange, setTimeRange] = useState("Week");
  const [chartData, setChartData] = useState({
    series: [], categories: [], rawData: [],
  });
  const [categoryList, setCategoryList] = useState([allObj]);
  const [filterValues, setFilterValues] = useState({ category: allObj });

  // const mapTimeRangeToKey = (range) => {
  //   const keyMap = {
  //     "3m": "last3months",
  //     "6m": "last6months",
  //     "9m": "last9months",
  //     "12m": "last12months",
  //   };
  //   return keyMap[range] || "last6months";
  // };

  // useEffect(() => {
  //   dispatch(fetchServiceDashboardData());
  // }, [dispatch]);

  useEffect(() => {
    if (!top5ServicesByRevenue || !top5ServicesByRevenue?.top5Services) return;
    const data = top5ServicesByRevenue?.top5Services
    setChartData({
      categories: data?.map((item) => item?.serviceName ?? ''),
      series: [{ name: "Revenue", data: data?.map((item) => item?.serviceRevenue ?? 0) }],
      rawData: data || [],
    });
  }, [top5ServicesByRevenue]);

  const options = useMemo(() => ({
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: {
      bar: {
        columnWidth: "40%",
        borderRadius: 4,
        distributed: true,
      },
    },
    colors: ["#0052CC", "#1ABC7B", "#FFA800", "#FF4C96", "#9B51E0"],
    dataLabels: { enabled: false },
    legend: { show: false },
    xaxis: {
      categories: chartData.categories,
      labels: { style: { fontSize: "13px" } },
    },
    yaxis: {
      labels: {
        formatter: (val) => {
          if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
          if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
          return val.toString();
        },
      },
    },
    grid: { strokeDashArray: 4 },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        const serviceData = chartData.rawData[dataPointIndex];
        if (!serviceData) return '';

        const serviceName = serviceData?.serviceName || '';
        const serviceRevenue = serviceData?.serviceRevenue ?? 0;
        const currentPeriodRevenue = serviceData?.currentPeriodRevenue ?? 0;
        const previousPeriodRevenue = serviceData?.previousPeriodRevenue ?? 0;
        const changePercent = serviceData?.changePercent ?? 0;

        const formatCurrency = (val) => `₹ ${Number(val).toLocaleString("en-IN")}`;
        const formatPercent = (val) => `${Number(val).toFixed(2)}%`;

        return (
          '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; background-color: #EBEBEB; color: #000; padding: 8px; margin: 0;">' +
          serviceName +
          "</div>" +
          '<div class="apexcharts-tooltip-series-group" style="order: 1; display: flex; background-color: #fff; padding: 8px;">' +
          '<div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #000;">' +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Service Revenue: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          formatCurrency(serviceRevenue) +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Current Period Revenue: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          formatCurrency(currentPeriodRevenue) +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Previous Period Revenue: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          formatCurrency(previousPeriodRevenue) +
          "</span>" +
          "</div>" +
          '<div class="apexcharts-tooltip-y-group">' +
          '<span class="apexcharts-tooltip-text-y-label">Change: </span>' +
          '<span class="apexcharts-tooltip-text-y-value">' +
          formatPercent(changePercent) +
          "</span>" +
          "</div>" +
          "</div>" +
          "</div>"
        );
      },
    },
  }), [chartData.rawData, chartData.categories]);

  return (
    <Box sx={{
      backgroundColor: "#fff",
      borderRadius: 2,
      p: 3,
      boxShadow: 1,
      maxWidth: 800,
      margin: "auto",
    }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight={600}>
          Top 5 services by revenue
        </Typography>
        <div className="flex-row-ali-cen">
          <div style={{ width: '150px' }} className="mr20">
            <CustomSelect
              list={categoryList}
              value={filterValues?.category}
              handleChange={(e, value) => {
                if (!value?.value) return
                setFilterValues({ ...filterValues, category: value })
              }}
              search
              label=""
            />
          </div>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(_, val) => val && setTimeRange(val)}
            size="small"
            sx={{
              backgroundColor: "#f0f2f5",
              borderRadius: "12px",
              "& .MuiToggleButtonGroup-grouped": {
                borderRadius: "7px",
                borderColor: "transparent",
                color: "#333",
                transition: "none",
                "&.Mui-selected": {
                  backgroundColor: "#005EB8",
                  color: "#fff",
                },
              },
            }}
          >
            <ToggleButton value="Week">Week</ToggleButton>
            <ToggleButton value="Month">Month</ToggleButton>
            <ToggleButton value="Quarter">Quarter</ToggleButton>
            <ToggleButton value="Year">Year</ToggleButton>
          </ToggleButtonGroup>
        </div>
      </Box>

      <Chart options={options} series={chartData?.series} type="bar" height={300} />
    </Box>
  );
};

export default RevenueByServiceChart;